import { ArrowLeftOutlined } from '@ant-design/icons';
import { FetchBaseQueryError, skipToken } from '@reduxjs/toolkit/dist/query';
import { Button, Dropdown, MenuProps, message, Modal, notification, Space, Typography } from 'antd';
import { useFormikContext } from 'formik';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { SparingRequestPayload } from 'models/SparingRequests';
import { useGetSparingRequestConfigurationTypesQuery } from 'redux/services/calvin/sparingRequestConfigTypes';
import { useDeleteSparingRequestMutation, useGetSparingRequestQuery } from 'redux/services/twittlebugs/sparingRequestsApi';
import { setIsExit, setIsPost } from 'redux/slices/formSlice';
import { colors, toRgb } from 'styles/colors';
import { getDefectiveReturnsRequiredInfoErrors } from 'utils/defectiveReturns';
import '../../../styles/checkmarkStyle.less';
import '../../../styles/waveAnimation.css';

const styles: InlineStylesModel = {
  container: {
    width: '100%',
    justifyContent: 'space-between',
    marginBottom: 8
  },
  title: {
    marginBottom: 0,
    whiteSpace: 'nowrap'
  },
  draftButton: {
    color: toRgb(colors.raisinBlackLight),
    backgroundColor: '#EED202',
    border: 'none'
  },
  requestButton: {
    color: toRgb(colors.culturedWhite),
    backgroundColor: toRgb(colors.illuminatingEmerald),
    border: 'none'
  }
};

export enum CreateRequestMenu {
  RequestDetails = 'request-details',
  InstallLocation = 'install-location',
  Products = 'products',
  ShippingLocation = 'shipping-location',
  ShippingDetails = 'shipping-details',
  NotesSection = 'more-details',
  ReviewOrder = 'review-order'
}

export interface LineItemErrorObject {
  key: string;
  lineProductName: string;
  lineStatus?: boolean;
  lineMessage?: string;
  error?: FetchBaseQueryError;
}

export const ActionControls = (): JSX.Element => {
  /* ******************** Hooks ******************** */
  const nav = useNavigate();
  const { id } = useParams();
  const { pathname } = useLocation();
  const { data } = useGetSparingRequestQuery(id || skipToken);
  const { values, isSubmitting, submitForm, validateForm } = useFormikContext<SparingRequestPayload>();
  const [deleteSparingRequest] = useDeleteSparingRequestMutation();
  const { data: requestTypesData, isLoading: isRequestTypesLoading } = useGetSparingRequestConfigurationTypesQuery();

  const dispatch = useDispatch();

  // const isSlaValid = values.lineItems.every((line) => line.quantityOrdered === line.quantity && line.serviceLevelAgreement);

  const isSlaValid = values.lineItems
    .map((line) => {
      if (!line?.subLines?.length) return false;
      const totalSublineQty = (line?.subLines ?? [])?.reduce((sum, curr) => sum + (curr.quantity ?? 0), 0);

      return totalSublineQty === line.quantity;
    })
    .every((item) => item);

  // Check custom required fields based on sparing request type
  const selectedType = requestTypesData?.data?.find((t) => t.id === values.sparingRequestTypeId);

  const customFieldsValid = selectedType
    ? (!selectedType.isReference1Required || values.customerReferenceNumber?.trim()) &&
      (!selectedType.isReference2Required || values.customerPurchaseOrderNumber?.trim()) &&
      (!selectedType.isEndCustomerVisible || !selectedType.isEndCustomerVisibleRequired || values.endCustomer?.trim())
    : true;

  // MDSI1-4304: Return Part Number / Return Serial Number on defective returns. Gates Post only -
  // the Save paths below deliberately ignore it so a draft stays saveable while it is incomplete.
  const defectiveReturnsRequiredInfo = getDefectiveReturnsRequiredInfoErrors({
    items: values.defectiveReturns?.items,
    lineItems: values.lineItems,
    requestType: selectedType
  });

  const phoneDigits = (values.contactPhoneNumber || '').replace(/\D/g, '');
  const isPhoneValid = phoneDigits.length === 10;

  const isPostValid = Boolean(
    values.sparingRequestTypeId &&
      values.shippingLocationId &&
      values.outageLocationId &&
      !!values.lineItems?.length &&
      isSlaValid &&
      isPhoneValid &&
      values.contactName &&
      values.contactEmail &&
      customFieldsValid &&
      defectiveReturnsRequiredInfo.isValid
  );

  /* ******************** Functions ******************** */

  const handleGoBack = (): void => {
    nav('/');
  };

  // const currentLocation = useMemo(() => {
  //   if (pathname.includes(CreateRequestMenu.InstallLocation)) {
  //     return CreateRequestMenu.InstallLocation;
  //   } else if (pathname.includes(CreateRequestMenu.Products)) {
  //     return CreateRequestMenu.Products;
  //   } else if (pathname.includes(CreateRequestMenu.ShippingLocation)) {
  //     return CreateRequestMenu.ShippingLocation;
  //   } else if (pathname.includes(CreateRequestMenu.ShippingDetails)) {
  //     return CreateRequestMenu.ShippingDetails;
  //   } else if (pathname.includes(CreateRequestMenu.NotesSection)) {
  //     return CreateRequestMenu.NotesSection;
  //   } else if (pathname.includes(CreateRequestMenu.ReviewOrder)) {
  //     return CreateRequestMenu.ReviewOrder;
  //   } else if (pathname.includes(CreateRequestMenu.RequestDetails)) {
  //     return CreateRequestMenu.RequestDetails;
  //   } else if (pathname.includes('request-info')) {
  //     return 'request-info';
  //   } else {
  //     return '';
  //   }
  // }, [pathname]);

  const handleSaveAndExit = async (): Promise<void> => {
    // A draft save is never a post, whatever an earlier blocked Post attempt left behind.
    dispatch(setIsPost(false));

    // Check if custom required fields are valid before saving
    if (!customFieldsValid) {
      const missingFields: string[] = [];

      if (selectedType) {
        if (selectedType.isReference1Required && !values.customerReferenceNumber?.trim()) {
          missingFields.push(selectedType.reference1Label || 'Primary Reference Number');
        }

        if (selectedType.isReference2Required && !values.customerPurchaseOrderNumber?.trim()) {
          missingFields.push(selectedType.reference2Label || 'Secondary Reference Number');
        }

        if (selectedType.isEndCustomerVisible && selectedType.isEndCustomerVisibleRequired && !values.endCustomer?.trim()) {
          missingFields.push('End Customer');
        }
      }

      notification.error({
        message: 'Required Fields Missing',
        description: `Please fill in the following required fields: ${missingFields.join(', ')}`,
        className: 'custom-class',
        style: {
          width: 600
        },
        duration: 5
      });

      return;
    }
    dispatch(setIsExit(true));

    // Same trap as Post below: formik skips onSubmit when validate returns errors, and isExit is only
    // reset from inside onSubmit, so a blocked Save & Close would turn the next plain Save into one.
    const errors = await validateForm();

    if (Object.keys(errors).length > 0) dispatch(setIsExit(false));

    await submitForm();
  };

  const handleDelete: React.MouseEventHandler<HTMLElement> = async (event): Promise<void> => {
    if (id) {
      Modal.error({
        width: 600,
        okText: 'Delete',
        okType: 'danger',
        onOk: async () => {
          try {
            event.stopPropagation();
            await deleteSparingRequest({ id, params: { wouldYouLikeToPlayAGame: false } });
            message.success('Sparing request successfully deleted');
            nav('/');
          } catch (error) {
            message.error((error as { data: { errorMessage: string } }).data.errorMessage);
          }
        },
        cancelText: 'Cancel',
        onCancel: () => {
          console.error('canceled popup');
        },
        closable: true,
        title: <Typography.Title level={5}>Delete Request {data?.requestNumber} ?</Typography.Title>,
        content: <Typography.Text>Are you sure you want to delete this Sparing Request? You can still recover it in the Recycle Bin...</Typography.Text>
      });
    }
  };

  const items: MenuProps['items'] = [
    {
      label: 'Save',
      key: '1',
      // MDSI1-4304: saving before the request types load would send whatever the defective returns
      // table has not reconciled yet. Wait for them, so the payload does not depend on a query race.
      disabled: isRequestTypesLoading,
      onClick: () => {
        // A plain Save is never a post and never an exit, whatever an earlier blocked attempt left behind.
        dispatch(setIsPost(false));
        dispatch(setIsExit(false));

        // Check if custom required fields are valid before saving
        if (!customFieldsValid) {
          const missingFields: string[] = [];

          if (selectedType) {
            if (selectedType.isReference1Required && !values.customerReferenceNumber?.trim()) {
              missingFields.push(selectedType.reference1Label || 'Primary Reference Number');
            }

            if (selectedType.isReference2Required && !values.customerPurchaseOrderNumber?.trim()) {
              missingFields.push(selectedType.reference2Label || 'Secondary Reference Number');
            }

            if (selectedType.isEndCustomerVisible && selectedType.isEndCustomerVisibleRequired && !values.endCustomer?.trim()) {
              missingFields.push('End Customer');
            }
          }

          notification.error({
            message: 'Required Fields Missing',
            description: `Please fill in the following required fields: ${missingFields.join(', ')}`,
            className: 'custom-class',
            style: {
              width: 600
            },
            duration: 5
          });

          return;
        }
        submitForm();
      }
    }
  ];

  /* ******************** Render ******************** */
  return (
    <Space size={['large', 'small']} wrap style={styles.container}>
      <Typography.Title style={styles.title} level={4}>
        {id ? 'Edit Sparing Request' : 'Create Sparing Request'}
      </Typography.Title>
      <Space size="small" wrap>
        <Button onClick={handleGoBack} icon={<ArrowLeftOutlined />}>
          Back to Search
        </Button>
        {id && (
          <Button onClick={handleDelete} type="ghost" danger>
            Delete
          </Button>
        )}
        <Dropdown.Button type="primary" onClick={handleSaveAndExit} disabled={isRequestTypesLoading} loading={isSubmitting} menu={{ items }}>
          Save &amp; Close
        </Dropdown.Button>
        <Button
          style={isPostValid ? { background: 'rgb(78, 147, 122)', color: 'white' } : undefined}
          onClick={async (): Promise<void> => {
            dispatch(setIsPost(true));

            // Formik skips onSubmit entirely when validate returns errors, and every reset of this
            // flag lives inside onSubmit - so a blocked Post would leave it stuck on and the next
            // Save would be posted. Clear it here before the blocked submit attempt.
            const errors = await validateForm();

            // The layouts raise the MDSI1-4304 defective returns error only while isPost is set, and
            // this validateForm() call still sees the render from before the dispatch above - so ask
            // the shared helper directly rather than waiting for that error to appear here.
            if (Object.keys(errors).length > 0 || !defectiveReturnsRequiredInfo.isValid) dispatch(setIsPost(false));

            await submitForm();
          }}
          disabled={!isPostValid || isRequestTypesLoading}
          loading={isSubmitting}>
          Post Request
        </Button>
      </Space>
    </Space>
  );
};
